import env from "$root/env";
import { sphereAIAvailable } from "$root/functional-areas/sphere-ai/config";
import { UserCircle } from "$root/functional-areas/acl/UserCircle";
import { notificationCenter$ } from "$root/functional-areas/notification-center/NotificationStore";
import { PortfolioStudioSettingTabEnum } from "$root/functional-areas/portfolio-studio-settings/tab-enum";
import { useUserValue } from "$root/functional-areas/user";
import { Intercom } from "$root/third-party-integrations/initIntercom";
import { qualifier } from "$root/utils";
import { Icon, type IconName } from "@mdotm/shadcn/legacy/components";
import { WithAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { Sandwich } from "@mdotm/shadcn/mdotm/layout";
import { useLocation } from "react-router";
import { hasAccess } from "../AuthorizationGuard";
import { typedUrlForRoute } from "../PlatformRouter/RoutesDef";
import { AdminPanelModule } from "./AdminPanelModule";
import { mainActiveSidebar$, type AppDrawerModule } from "./AppDrawer-shared";
import { useAppVersionCheck } from "./useAppVersionCheck";
import { MarketInsightModule } from "./MarketInsightModule";
import { PortfolioStudioModule } from "./PortfolioStudioModule";
import { SettingsModule } from "./SettingsModule";
import { StoryfolioModule } from "./StofyfolioModule";
import { UserModule } from "./UserModule";
import { getPortfolioStudioUrl } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";

export function useAppDrawerModules(): { top: AppDrawerModule[]; bottom: AppDrawerModule[] } {
	const user = useUserValue();
	const location = useLocation();
	const appVersionCheck = useAppVersionCheck();
	const updateAvailable = appVersionCheck.data?.updateAvailable ?? false;

	return {
		top: [
			{
				enabled: hasAccess(user, { requiredService: "INVESTMENTS" }),
				icon: "portfolio-studio" satisfies IconName,
				name: "PortfolioStudio",
				title: "Portfolio Studio",
				href: getPortfolioStudioUrl(),
				children: <PortfolioStudioModule />,
				active:
					(location.pathname.startsWith("/portfolio") && !location.pathname.startsWith("/portfolio_studio_settings")) ||
					location.pathname.startsWith("/universe_details") ||
					location.pathname.startsWith("/workspace_market") ||
					location.pathname.startsWith("/legacy_benchmarks"),
			},
			{
				enabled:
					hasAccess(user, { requiredService: "REPORTS" }) ||
					hasAccess(user, { requiredService: "SIGNALS" }) ||
					hasAccess(user, { requiredService: "GLOBAL_RISK_MAP" }) ||
					hasAccess(user, { requiredService: "CUSTOM_REPORT_CB1" }),
				icon: "market-insight" satisfies IconName,
				name: "MarketInsight",
				title: "Market Insight",
				href: typedUrlForRoute("MarketDashboard", {}),
				children: <MarketInsightModule />,
				active:
					location.pathname.startsWith("/market") ||
					location.pathname.startsWith("/outlook") ||
					location.pathname.startsWith("/asset_class") ||
					location.pathname.startsWith("/dashboard") ||
					location.pathname.startsWith("/regime_analysis_tool") ||
					location.pathname.startsWith("/custom_reports"),
			},
			{
				icon: "Storyfolio" satisfies IconName,
				name: "Storyfolio",
				title: "Storyfolio",
				href: typedUrlForRoute("Storyfolio/Studio", {}),
				enabled:
					hasAccess(user, { requiredService: "INVESTMENTS_REPORT_TEMPLATE_EDITOR" }) ||
					hasAccess(user, { requiredService: "COMMENTARY_BUILDER" }),
				children: <StoryfolioModule />,
				active:
					(location.pathname.startsWith("/portfolio_studio_settings") &&
						location.search.includes(`tab=${PortfolioStudioSettingTabEnum.ReportCustomisation}`)) ||
					location.pathname.startsWith("/storyfolio") ||
					location.pathname.startsWith("/report_editor") ||
					location.pathname.startsWith("/portfolio_studio_settings/report-editor"),
			},
			{
				enabled: true,
				icon: "Settings" satisfies IconName,
				name: "Settings",
				title: "Settings",
				href: typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {}),
				children: <SettingsModule />,
				active:
					(location.pathname.startsWith("/portfolio_studio_settings") &&
						!location.search.includes(`tab=${PortfolioStudioSettingTabEnum.ReportCustomisation}`)) ||
					location.pathname.startsWith(typedUrlForRoute("Notification", {})),
			},
			{
				enabled: hasAccess(user, { requiredRoles: ["ROOT"] }),
				icon: "Optimize" satisfies IconName,
				name: "AdminPanel",
				title: "Admin Panel",
				href: typedUrlForRoute("AdvanceSettings/UserSettingsPanel", {}),
				children: <AdminPanelModule />,
				active:
					location.pathname.startsWith(typedUrlForRoute("AdvanceSettings/UserSettingsPanel", {})) ||
					location.pathname.startsWith(typedUrlForRoute("AdvanceSettings/PlatformInfo", {})),
			},
		],
		bottom: [
			{
				// Persistent "a newer version of Sphere is available" indicator. Only appears once the
				// deployed version differs from the one this tab loaded; clicking reloads to update.
				enabled: updateAvailable,
				icon: (
					<Sandwich style={{ width: 20, height: 20 }}>
						<Icon icon="Reload" size={20} />

						<span className="flex absolute h-2 w-2 top-0 right-0 -mr-0.5">
							<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-(--palette-D500) opacity-75" />
							<span className="relative inline-flex rounded-full h-2 w-2 bg-(--palette-D500)" />
						</span>
					</Sandwich>
				),
				name: "AppUpdate",
				title: "New version available - Click to reload",
				onClick() {
					window.location.reload();
				},
				active: false,
			},
			{
				enabled: sphereAIAvailable() && hasAccess(user, { requiredService: "ASK_SPHERE" }),
				icon: "sphere-ai" satisfies IconName,
				name: "AskSphere",
				title: "Ask Sphere",
				onClick() {
					writeAtom(mainActiveSidebar$, (cur) => (cur === "chat" ? null : "chat"));
				},
				active: false,
			},
			{
				icon: (
					<Sandwich style={{ width: 20, height: 20 }}>
						<Icon icon="Property-1Notifications" size={20} />

						<WithAtom atom$={notificationCenter$}>
							{(notificationCenter) =>
								!notificationCenter.markAsRead && (
									<span className="flex absolute h-2 w-2 top-0 right-0 -mr-0.5">
										<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-(--palette-D500) opacity-75" />
										<span className="relative inline-flex rounded-full h-2 w-2 bg-(--palette-D500)" />
									</span>
								)
							}
						</WithAtom>
					</Sandwich>
				),
				name: "Notifications",
				title: "Notifications",
				enabled:
					env.featureFlags.notificationCenter.enabled && hasAccess(user, { requiredService: "NOTIFICATION_CENTER" }),
				onClick() {
					writeAtom(mainActiveSidebar$, (cur) => (cur === "notifications" ? null : "notifications"));
				},
				active: false,
			},
			{
				enabled: hasAccess(user, { requiredService: "HELP_CENTER" }),
				icon: "Tooltip-questionmark" satisfies IconName,
				name: "Help",
				title: "Help",
				onClick() {
					if ((document.querySelector<HTMLDivElement>(".intercom-messenger-frame")?.children.length ?? 0) > 0) {
						Intercom.hide();
					} else {
						Intercom.showHelp();
					}
				},
				active: false,
			},
			{
				enabled: true,
				background: themeCSSVars.palette_N800,
				icon: (
					<UserCircle
						data-qualifier={qualifier.appDrawer.profilePhoto}
						size="medium"
						palette="dark"
						variant="name"
						firstName={user.name ?? ""}
						lastName={user.surname ?? ""}
					/>
				),
				name: "Account",
				title: "User Settings",
				href: typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {}),
				children: <UserModule />,
				active: false,
			},
		],
	};
}
